import React, { useState, useEffect } from 'react';

export default function WorkbenchHeader({
  data,
  saveState = 'saved',
  lastSavedTime,
  onOpenReportModal,
  onOpenAiModal,
  onOpenPatientDrawer,
  onBackToQueue
}) {
  const [elapsedTime, setElapsedTime] = useState('');

  useEffect(() => {
    if (!data?.inoculationDate && !data?.receivedDate && !data?.orderDate) return;

    const calculateElapsed = () => {
      const start = new Date(data.inoculationDate || data.receivedDate || data.orderDate);
      const now = new Date();
      const diffMs = Math.max(0, now - start);

      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
      const mins = Math.floor((diffMs / (1000 * 60)) % 60);

      const dayDisplay = days > 0 ? `Day ${days + 1}` : 'Day 1';
      setElapsedTime(`${dayDisplay} • ${days * 24 + hours}h ${mins}m`);
    };

    calculateElapsed();
    const interval = setInterval(calculateElapsed, 30000);
    return () => clearInterval(interval);
  }, [data]);

  if (!data) return null;

  const getStatusBadge = () => {
    if (data.approved)         return { label: '✓ Released',        cls: 'released' };
    if (data.isCriticalAlert)  return { label: '⚠ Critical Alert',  cls: 'critical' };
    if (data.cultureStatus === 'Growth') {
      const isolateCount = data.isolates?.length || 0;
      if (isolateCount === 0) return { label: 'ID Pending',          cls: 'pending' };
      const astTested = (data.isolates || [])
        .flatMap((i) => i.astResults || [])
        .filter((r) => r.measuredValue !== null && r.measuredValue !== undefined).length;
      if (astTested === 0)    return { label: 'AST Pending',         cls: 'pending' };
      return                         { label: 'Review Pending',      cls: 'review' };
    }
    if (data.cultureStatus === 'No Growth') return { label: 'No Growth (Sterile)', cls: 'sterile' };
    if (data.inoculationDate || data.cultureStatus === 'Pending')
                                            return { label: 'Incubating',           cls: 'incubating' };
    if (data.receivedDate)                  return { label: 'Processing',           cls: 'processing' };
    return                                         { label: 'Received',             cls: 'processing' };
  };

  const badge = getStatusBadge();

  return (
    <header className="wb-patient-strip">
      {/* LEFT — Back + Patient Identity */}
      <div className="wb-patient-strip-left">
        <button
          onClick={onBackToQueue}
          className="wb-btn-back-queue"
          title="Return to Microbiology Worklist Queue"
        >
          ← Worklist
        </button>

        <div className="wb-patient-primary-meta">
          {/* Row 1: Name + accession IDs */}
          <div className="wb-patient-title-row">
            <span className="wb-patient-name-heading">
              {data.patientName || 'Anonymous Patient'}
            </span>
            <span className="wb-accession-chip">
              UHID: <strong>{data.patientID || '—'}</strong>
            </span>
            <span className="wb-accession-chip">
              Lab No: <strong>{data.ledgerTransactionNo || '—'}</strong>
            </span>
          </div>

          {/* Row 2: Clinical detail chips */}
          <div className="wb-patient-badges-row">
            <span className="wb-clinical-chip">{data.ageDisplay || 'Age —'}</span>
            <span className="wb-clinical-chip">{data.gender || '—'}</span>
            <span className="wb-clinical-chip specimen">{data.specimenName || 'Specimen'}</span>
            <span className="wb-clinical-chip test">
              {data.testName || data.investigationName || 'Microbiology Culture'}
            </span>
            {data.barcodeNo && (
              <span className="wb-clinical-chip barcode">SIN: {data.barcodeNo}</span>
            )}
            <span className="wb-meta-pill">
              Clinician: <strong>{data.doctorName || 'SELF / DIRECT REFERRAL'}</strong>
            </span>
            <button
              className="wb-patient-context-link"
              onClick={onOpenPatientDrawer}
              title="View complete clinical history and patient profile"
            >
              Patient Context →
            </button>
          </div>
        </div>
      </div>

      {/* RIGHT — Timer · Status · Save · Actions */}
      <div className="wb-patient-strip-right">
        {/* Incubation TAT Timer */}
        <div className="wb-timer-pill" title="Culture Incubation Duration / TAT">
          <span className="wb-timer-indicator-dot" />
          <span>{elapsedTime || 'Incubation Active'}</span>
        </div>

        {/* Dynamic Status Badge */}
        <span className={`wb-status-tag ${badge.cls}`}>{badge.label}</span>

        {/* Auto-save status */}
        {saveState === 'saving' && (
          <span className="wb-save-indicator saving">● Saving…</span>
        )}
        {saveState === 'saved' && (
          <span className="wb-save-indicator saved">
            ✓ Saved{lastSavedTime ? ` at ${lastSavedTime}` : ''}
          </span>
        )}
        {saveState === 'unsaved' && (
          <span className="wb-save-indicator unsaved">● Unsaved</span>
        )}

        {/* Preview Report CTA */}
        <button
          className="wb-btn-preview-report"
          onClick={onOpenReportModal}
          title="Preview Official Diagnostic Report"
        >
          Preview Report
        </button>
      </div>
    </header>
  );
}
